import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { playWeaponFireLocal } from '@sstk/weapon-kit';
import { useGunsmithStore, type AudioLayerKey } from '../store/gunsmithStore';
import type { WeaponAudioLayer } from '../../../engine/content/schema/weapon';
import {
  DEFAULT_BODY_SAMPLE_BY_ARCHETYPE,
  DEFAULT_CRACK_SAMPLE_BY_ARCHETYPE,
  DEFAULT_IMPACT_SAMPLE_BY_ARCHETYPE,
  DEFAULT_SWING_SAMPLE_BY_ARCHETYPE,
  DEFAULT_TAIL_SAMPLE_BY_ARCHETYPE,
  gunsmithAudioResolver,
} from '../audio/audioResolver';
import { Slider } from './controls';

const LAYER_LABELS: Record<AudioLayerKey, string> = { crack: 'Crack', body: 'Body', tail: 'Tail', swing: 'Swing', impact: 'Impact' };
const LAYER_HINTS: Record<AudioLayerKey, string> = {
  crack: 'The sharp transient at the very start of the shot.',
  body: "The weapon's own tone underneath the transient.",
  tail: 'How the sound decays after the shot.',
  swing: "The blade's own whoosh, the melee equivalent of a gunshot's crack.",
  impact: 'The contact hit, the melee equivalent of a gunshot\'s body.',
};

/** One real, bundled default sample per archetype for each layer, keyed the same way as {@link DEFAULT_CRACK_SAMPLE_BY_ARCHETYPE}. */
const DEFAULT_SAMPLE_BY_LAYER: Record<AudioLayerKey, Record<string, string>> = {
  crack: DEFAULT_CRACK_SAMPLE_BY_ARCHETYPE,
  body: DEFAULT_BODY_SAMPLE_BY_ARCHETYPE,
  tail: DEFAULT_TAIL_SAMPLE_BY_ARCHETYPE,
  swing: DEFAULT_SWING_SAMPLE_BY_ARCHETYPE,
  impact: DEFAULT_IMPACT_SAMPLE_BY_ARCHETYPE,
};

/** A gunshot's three layers, shown for any archetype with no real melee default -- the common case. */
const FIREARM_LAYERS = ['crack', 'body', 'tail'] as const;
/** A melee weapon's two layers, shown only for an archetype with a real swing/impact default (see DEFAULT_SWING_SAMPLE_BY_ARCHETYPE). */
const MELEE_LAYERS = ['swing', 'impact'] as const;

function LayerRow({
  layerKey,
  layer,
  defaultSampleId,
  onChange,
}: {
  layerKey: AudioLayerKey;
  layer: WeaponAudioLayer | undefined;
  /** A real, bundled sample id worth offering as a one-click starting point for this layer, if one exists. */
  defaultSampleId?: string;
  onChange: (value: WeaponAudioLayer | undefined) => void;
}) {
  const [sampleIdDraft, setSampleIdDraft] = useState(layer?.sampleId ?? '');

  // Resync when the layer changes for a reason other than this row's own
  // commit() (Clear, undo/redo, or switching the active weapon while this
  // panel stays mounted) -- otherwise the input keeps showing stale text
  // for a layer it no longer describes. Keyed on the sample id itself, not
  // the layer object reference, so an unrelated gain/pitch edit (which
  // produces a new layer object but leaves sampleId untouched) does not
  // clobber a still-in-progress, not-yet-blurred edit.
  useEffect(() => {
    setSampleIdDraft(layer?.sampleId ?? '');
  }, [layer?.sampleId]);

  const commit = (value: string) => {
    setSampleIdDraft(value);
    const trimmed = value.trim();
    if (!trimmed) { onChange(undefined); return; }
    onChange({ ...(layer ?? {}), sampleId: trimmed });
  };

  return (
    <div style={{ border: '1px solid var(--ss-border-soft, rgba(255,255,255,0.08))', borderRadius: 8, padding: '9px 10px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600 }}>{LAYER_LABELS[layerKey]}</div>
          <div style={{ fontSize: 10.5, color: 'var(--ss-text-3, #6d747e)' }}>{LAYER_HINTS[layerKey]}</div>
        </div>
        {layer && (
          <button type="button" className="ss-btn ss-btn--ghost ss-btn--sm" onClick={() => onChange(undefined)}>
            Clear
          </button>
        )}
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 92, flexShrink: 0, fontSize: 11.5, color: 'var(--ss-text-2, #8b929c)' }}>Sample id</span>
        <input
          className="ss-input"
          placeholder="e.g. assault-crack-01"
          value={sampleIdDraft}
          spellCheck={false}
          onChange={(e) => setSampleIdDraft(e.target.value)}
          onBlur={() => commit(sampleIdDraft)}
          style={{ flex: 1, padding: '6px 9px', fontSize: 11.5, borderRadius: 7 }}
        />
      </label>
      {defaultSampleId && sampleIdDraft.trim() !== defaultSampleId && (
        <button
          type="button"
          className="ss-btn ss-btn--ghost ss-btn--sm"
          onClick={() => commit(defaultSampleId)}
          title="Use a real, bundled CC0 recording matched to this weapon's archetype"
          style={{ alignSelf: 'flex-start' }}
        >
          Use real sample ({defaultSampleId})
        </button>
      )}
      {layer && (
        <>
          <Slider
            label="Gain"
            value={layer.gain ?? 1}
            min={0}
            max={2}
            step={0.01}
            format={(v) => v.toFixed(2)}
            onChange={(v) => onChange({ ...layer, gain: v })}
          />
          <Slider
            label="Pitch jitter"
            value={layer.pitchJitter ?? 0}
            min={0}
            max={0.3}
            step={0.01}
            format={(v) => `±${Math.round(v * 100)}%`}
            onChange={(v) => onChange({ ...layer, pitchJitter: v })}
          />
        </>
      )}
    </div>
  );
}

export function AudioPanel() {
  const weapons = useGunsmithStore((s) => s.weapons);
  const activeWeaponId = useGunsmithStore((s) => s.activeWeaponId);
  const setAudioLayer = useGunsmithStore((s) => s.setAudioLayer);
  const setReverbSend = useGunsmithStore((s) => s.setReverbSend);

  const listenerRef = useRef<THREE.AudioListener | null>(null);
  const [firing, setFiring] = useState(false);
  const [fireError, setFireError] = useState<string | null>(null);

  const found = weapons.find((w) => w.id === activeWeaponId);
  if (!found) {
    return <p style={{ padding: 14, fontSize: 12, color: 'var(--ss-text-3, #6d747e)' }}>Select or create a weapon first.</p>;
  }
  const active = found;

  // A melee archetype (one with a real swing/impact default, e.g. shiv) gets
  // that pair instead of a gunshot's crack/body/tail; every other archetype,
  // including one this workspace has never seen before, gets the firearm
  // layers -- the common case, and a safe default for an unrecognised id.
  const isMeleeArchetype = active.archetype in DEFAULT_SWING_SAMPLE_BY_ARCHETYPE || active.archetype in DEFAULT_IMPACT_SAMPLE_BY_ARCHETYPE;
  const layerKeys = isMeleeArchetype ? MELEE_LAYERS : FIREARM_LAYERS;
  const hasAnyLayer = layerKeys.some((key) => Boolean(active.audio?.[key]));

  async function testFire() {
    if (!active) return;
    // AudioContext creation/resume must follow a real user gesture (browser
    // autoplay policy) - this button click is that gesture, so the listener
    // is created lazily here rather than at module load or panel mount.
    if (!listenerRef.current) listenerRef.current = new THREE.AudioListener();
    setFiring(true);
    setFireError(null);
    try {
      await playWeaponFireLocal(active.audio, gunsmithAudioResolver, listenerRef.current);
    } catch (error) {
      setFireError(error instanceof Error ? error.message : "Could not play - check the sample id exists (try one of the 'Use real sample' shortcuts above for a verified one).");
    } finally {
      setFiring(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '12px 14px', overflowY: 'auto', flex: 1 }}>
      <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <span style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.13em', color: 'var(--ss-text-2, #8b929c)' }}>
          {isMeleeArchetype ? 'Melee layers' : 'Fire layers'}
        </span>
        {layerKeys.map((key) => (
          <LayerRow
            key={key}
            layerKey={key}
            layer={active.audio?.[key]}
            defaultSampleId={DEFAULT_SAMPLE_BY_LAYER[key][active.archetype]}
            onChange={(value) => setAudioLayer(key, value)}
          />
        ))}
      </section>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.13em', color: 'var(--ss-text-2, #8b929c)' }}>Reverb</span>
        <Slider
          label="Send"
          value={active.audio?.reverbSend ?? 0}
          min={0}
          max={1}
          step={0.01}
          format={(v) => `${Math.round(v * 100)}%`}
          onChange={(v) => setReverbSend(v)}
        />
        <p style={{ fontSize: 10.5, color: 'var(--ss-text-3, #6d747e)', margin: 0 }}>
          Send level to whichever convolution reverb the active map provides - the document only says how much to use.
        </p>
      </section>

      <section style={{ paddingTop: 6, borderTop: '1px solid var(--ss-border-soft, rgba(255,255,255,0.08))' }}>
        <button
          type="button"
          className="ss-btn ss-btn--primary ss-btn--sm"
          onClick={() => void testFire()}
          disabled={firing || !hasAnyLayer}
          title={hasAnyLayer ? 'Play a real shot using every configured layer' : 'Configure at least one layer with a sample id first'}
          style={{ width: '100%' }}
        >
          {firing ? 'Firing...' : 'Test fire'}
        </button>
        {fireError && <p style={{ fontSize: 11, color: 'var(--ss-danger, #d0453f)', marginTop: 8 }}>{fireError}</p>}
      </section>
    </div>
  );
}
